import styled from "@emotion/styled";
import { type CommunicationHistory } from "@repo/api";
import {
  Badge,
  LightMode,
  Pill,
  Text,
  Variables,
  documentIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { localizeDateAndTime } from "@repo/ui";

import { PillContainer } from "./sendCommunication/EmailMultiInput";

const HistoryCard = styled.div`
  border: 1px solid ${LightMode.colourNeutral3};
  border-radius: ${Variables.radiusS};
  padding: ${Variables.space125};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${Variables.space75};
`;

const MetaContainer = styled.div`
  margin-top: ${Variables.space25};
`;

const BodyPreviewContainer = styled.div`
  margin-top: ${Variables.space75};
  max-height: 200px;
  overflow-y: auto;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${Variables.space50};
  margin-top: ${Variables.space25};
`;

interface Props {
  item: CommunicationHistory;
}

export const CommunicationHistoryItemCard = ({ item }: Props) => {
  const badgeColour = item.status === "SENT" ? "green" : "red";
  const { dateFnsLocale } = useTranslation();

  return (
    <HistoryCard>
      <HeaderRow>
        <Text textStyle="Heading.S">{item.subject}</Text>
        <Badge
          colour={badgeColour}
          label={t(`label.communication_status_${item.status.toLowerCase()}`)}
          size="small"
        />
      </HeaderRow>

      <MetaContainer>
        <MetaRow>
          <Text textStyle="Heading.XS" color="text.secondary">
            {t("label.sent_by")}
          </Text>
          <Text textStyle="Body.S" color="text.secondary">
            {item.fromUser} {localizeDateAndTime(item.createdAt, dateFnsLocale)}
          </Text>
        </MetaRow>

        <MetaRow>
          <Text textStyle="Heading.XS">{t("label.to")}</Text>
          <Text textStyle="Body.S">{item.toEmail.join(", ")}</Text>
        </MetaRow>

        {item.ccEmail?.filter(Boolean).length > 0 && (
          <MetaRow>
            <Text textStyle="Heading.XS">{t("label.cc")}</Text>
            <Text textStyle="Body.S">
              {item.ccEmail.filter(Boolean).join(", ")}
            </Text>
          </MetaRow>
        )}
      </MetaContainer>

      <BodyPreviewContainer>
        <Text
          textStyle="Body.M"
          color="text.primary"
          dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
        />
      </BodyPreviewContainer>

      {item.attachmentFileNames && item.attachmentFileNames.length > 0 && (
        <PillContainer>
          {item.attachmentFileNames.map((fileName) => (
            <Pill
              key={fileName}
              label={fileName}
              leadingIcon={documentIcon}
              size="xsmall"
            />
          ))}
        </PillContainer>
      )}
    </HistoryCard>
  );
};
